import {Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import {MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {BehaviorSubject, Observable} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, take} from 'rxjs/operators';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';

import {ClientBankAccountHelperDataService} from '../../../services/client-bank-account-helper-data.service';
import {ClientBankAccountValidationService} from '../../../services/client-bank-account-validation.service';
import {ResourceService} from '../../../services/resource.service';
import {CountryModel} from '../../models/CountryModel';
import {TerraUtils} from '../../TerraUtils';
import {ClientBankAccountReqRes} from 'src/app/dashboard/models/bankAccount.model';
import {fadeInOut} from '../../animations';
import {CurrencyModel} from '../../models/CurrencyModel';
import {RadioBtn} from '../form/radio-buttons/radio-buttons.component';
import {PaymentType} from '../../enums/payment-type.enum';
import {CustomValidators} from '../../validators/custom.validators';
import {AppSettingsService, SubdomainEnum} from 'src/app/services/app-settings.service';
import {UserService} from 'src/app/services/shared/user.service';
import UnitAccountProvider from '../../enums/UnitAccountProvider.enum';
import CheckingOrSavingsType from '../../enums/CheckingOrSavingsType.enum';

@Component({
  selector: 'terra-client-bank-account',
  templateUrl: './client-bank-account.component.html',
  styleUrls: ['./client-bank-account.component.scss'],
  animations: [fadeInOut]
})
export class ClientBankAccountComponent extends OnDestroyMixin implements OnInit, OnChanges, OnDestroy {

  // This will tell the parent form that the clientBankAccount form is ready
  @Output() formReady = new EventEmitter<void>();

  @Output() changedBankFormat = new EventEmitter<boolean>();

  @Output() changedCountry = new EventEmitter<CountryModel>();

  @Output() currencyLearnMore = new EventEmitter<void>();

  private _isRecommendedCurrencySelected = new BehaviorSubject<boolean>(false);
  @Output() isRecommendedCurrencySelected = this._isRecommendedCurrencySelected.pipe(distinctUntilChanged(), shareReplay(1));

  private _preferredCurrency = new BehaviorSubject<CurrencyModel>(null);
  @Output() preferredCurrency = this._preferredCurrency.pipe(distinctUntilChanged(), shareReplay(1));

  @Input() externalForm: UntypedFormGroup;

  @Input() partialFormName: string;

  @Input() bankAccountModel: ClientBankAccountReqRes;

  @Input() isForceBankNameAndBranchCode = false;

  @Input() enablePreferredCurrency = false;

  @HostBinding('class.read-only-mode') @Input() isReadOnlyMode = false;

  @ViewChild('.rates-compare-table-wrapper') ratesCompareTableWrapper: ElementRef;

  accountNumberDraft = '';
  confirmAccountNumberDraft = '';

  ratesCompareTableHidden = true;
  CrePaymentType = PaymentType;
  paymentMethodDetails: RadioBtn[] = [
    {
      text: 'ACH',
      hint: null,
      value: PaymentType.Ach,
      disabled: false,
      checked: false
    },
    {
      text: 'Wire',
      hint: null,
      value: PaymentType.Wire,
      disabled: false,
      checked: false
    }
  ];

  get clientBankAccountForm() {
    return this.externalForm.get(this.partialFormName) as UntypedFormGroup;
  }

  get paymentTypeTooltip() {
    return `Select the method to receive payments:\nACH: Common option, processes in a few days.\nWire: Faster transfer, usually on the same day, higher fees.`;
  }

  // get IsRecommendedCurrencySelected() {
  //   const selectedCurrency = this.clientBankAccountForm.get('preferredCurrency').value;
  //   if (selectedCurrency && this.selectedCountry) {
  //     return this.selectedCountry.defaultCurrency.id === selectedCurrency.id;
  //   }
  //   return false;
  // }

  @ViewChild(MatAutocompleteTrigger)
  autocompleteCountry: MatAutocompleteTrigger;

  countries$ = this.resourceService.allCountries$;

  isCountryAutofilled = false;
  showInternalLoader = false;
  selectedCountry: CountryModel;
  showInternationalFields = false;
  disableLocalFormatTab = false;
  private formIsSet = false;
  showSubmitError = false;
  error: string;

  isIgnoreReadOnlyBankName = false;
  isIgnoreReadOnlyBankBranchCode = false;

  currencies$: Observable<CurrencyModel[]> = this.resourceService.getOutboundCurrencies()
    .pipe(
      map(response => response.filter(c => !c.isInbound)),
      shareReplay(1)
    );
  memoTooltipText = 'This Memo / Special Instruction also known as, OBI (Originating Beneficiary Information), "Message/Reference to Beneficiary" will be sent along with the wire information and will inform the beneficiary as to additional instructions (for example, which customer account number to apply the funds to).  If you are wiring to a brokerage firm that uses a bank to receive funds, such as National Financial Services or Charles Schwab, enter the "For Further Credit" (FFC) information in this field to ensure that your funds are applied to the proper brokerage account.';
  memoLength = TerraUtils.consts.validators.MEMO_MAX_LENGTH;
  CheckingOrSavingsType = CheckingOrSavingsType;
  checkingOrSavingsTypes = CheckingOrSavingsType.listAll();

  currencyCompareFn(c1: CurrencyModel, c2: CurrencyModel) {
    return c1 && c2 ? c1.id === c2.id : c1 === c2;
  }

  constructor(
    private resourceService: ResourceService,
    private helperService: ClientBankAccountHelperDataService,
    private validationService: ClientBankAccountValidationService,
    private formsBuilder: UntypedFormBuilder,
    private appSettings: AppSettingsService,
    private userService: UserService
  ) {
    super();
  }

  toggleUIFieldsLogic(event: MatTabChangeEvent) {
    this.clientBankAccountForm.updateValueAndValidity();
    // The Tab with index = 0 is the Local fields tab, and with index = 1 is with the international fields.
    this.showInternationalFields = event.index === 1;
    this.setFieldsAccordingToSelectedCountry();
    this.changedBankFormat.emit(this.showInternationalFields);
  }

  ngOnInit(): void {
    if (this.isReadOnlyMode && this.isForceBankNameAndBranchCode) {
      this.isForceBankNameAndBranchCode = false;
    }
    if (this.enablePreferredCurrency) {
      this.formReady.pipe(take(1), untilComponentDestroyed(this),
        switchMap(() => this.clientBankAccountForm.get('preferredCurrency').valueChanges),
      ).subscribe((selectedCurrency: CurrencyModel) => {
        // When changing the currency, if the currency is not the recommended, block the local format details (for Israel and GB)
        if (this.selectedCountry) {
          if (this.selectedCountry.code === TerraUtils.consts.countryCode.IL || this.selectedCountry.code === TerraUtils.consts.countryCode.GB) {
            if (this.selectedCountry.defaultCurrency.id !== selectedCurrency.id) {
              // block local format
              // this.setFieldsAccordingToSelectedCountry(true);
              this.showInternationalFields = true;
              this.resetILDomesticFields();
              this.setInternationalFields();
              // hide the local tab
              this.disableLocalFormatTab = true;

              // if the active tab isk the local format - switch to international (tab 1)
              if (!this.showInternationalFields) {
                this.showInternationalFields = true;
              }

            } else {
              // allow  local format
              // show the local tab
              this.disableLocalFormatTab = false;
            }
          }
        }
      });
    }

    this.disableWirePayMethod();
  }

  disableWirePayMethod() {

    const isGp = this.appSettings.subDomain === SubdomainEnum.GP;
    if (isGp) {
      this.userService.accountDetails$
        .pipe(
          take(1)
        )
        .subscribe(
          accountDetails => {
            if (accountDetails.organizationDetails.unitAccountProvider !== UnitAccountProvider.Thread) {
              this.paymentMethodDetails = [
                {
                  text: 'ACH',
                  hint: null,
                  value: PaymentType.Ach,
                  disabled: false,
                  checked: true
                },
                {
                  text: 'Wire',
                  hint: null,
                  value: PaymentType.Wire,
                  disabled: true,
                  checked: false
                }
              ];
            }
          }
        );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.bankAccountModel) {
      if (changes.bankAccountModel.firstChange) {
        this.generateForm();
        this.clientBankAccountForm.get('preferredCurrency').valueChanges.subscribe(preferredCurrency => {
          this._preferredCurrency.next(preferredCurrency);
          if (preferredCurrency && this.selectedCountry) {
            this._isRecommendedCurrencySelected.next(this.selectedCountry.defaultCurrency.id === preferredCurrency.id);
          }
        });
      }
      this.populateForm(this.bankAccountModel);
    }


    if (changes.enablePreferredCurrency) {
      if (changes.enablePreferredCurrency.currentValue === true) {
        this.clientBankAccountForm.get('preferredCurrency').setValidators([Validators.required]);
      } else {
        this.clientBankAccountForm.get('preferredCurrency').setValidators([]);
      }
    }
    if (this.isReadOnlyMode && this.isForceBankNameAndBranchCode) {
      this.isForceBankNameAndBranchCode = false;
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.helperService.ngUnsubscribe.next();
    this.helperService.ngUnsubscribe.complete();
  }

  countrySelected(country: CountryModel) {
    if (!country) {
      this.selectedCountry = null;
      this.changedCountry.emit(null);
    } else if ((!this.selectedCountry || country.id !== this.selectedCountry.id)) {

      this.showInternalLoader = true;

      this.selectedCountry = country;
      this.bankAccountModel.countryId = country.id;
      this.bankAccountModel.checkingOrSavingsType = CheckingOrSavingsType.Checking;
      // Tell the validation service that it should use the selected country for validation
      this.validationService.setCountry(country);

      this.changedCountry.emit(country);

      if (this.enablePreferredCurrency) {
        this.updateModelWithPreferredCurrency(country);
      }

      this.helperService.getBankAccountFields(country.code).subscribe(fields => {
        this.clientBankAccountForm.updateValueAndValidity();
        this.validationService.setBankAccountFields(fields);

        this.helperService.emitCountryChanged(country);
        this.initFormDataToSelectedCountry();

        this.showInternalLoader = false;
      });
    }
  }

  nonACHChange(): void {
    if (this.clientBankAccountForm.get('nonACH').value) {
      this.clientBankAccountForm.get('usabaRoutingNumber')?.reset();
      this.clientBankAccountForm.get('usabaRoutingNumber')?.disable();
      this.clientBankAccountForm.setValidators([this.minimumNumValuesRequired(1, ['frn', 'swift'])]);
      return;
    }
    this.clientBankAccountForm.get('frn')?.reset();
    this.clientBankAccountForm.get('swift')?.reset();
    this.clientBankAccountForm.get('usabaRoutingNumber')?.enable();
    this.clientBankAccountForm.setValidators([]);
    this.clientBankAccountForm.updateValueAndValidity();
  }

  setBankName(bankName: string) {
    this.clientBankAccountForm.controls.bankName.setValue(bankName, {
      emitEvent: false
    });
  }

  private generateForm() {
    // Generate the controls as component properties so that they can be accessed easily

    this.externalForm.setControl(
      this.partialFormName,
      this.formsBuilder.group({
        countryId: [null, Validators.required],
        preferredCurrency: [],
        bankName: [],
        accountNumber: [],
        confirmAccountNumber: [],
        memo: [null, [Validators.maxLength(this.memoLength)]],
        swift: [],
        bankCode: [],
        bankBranchCode: [],
        bankBranchName: [],
        bsb: [],
        canadaInstitutionNumber: [],
        canadaTransitNumber: [],
        ifsc: [],
        iban: [],
        ukSortCode: [],
        usabaRoutingNumber: [],
        paymentType: [],
        nonACH: false,
        frn: [],
        checkingOrSavingsType: CheckingOrSavingsType.Checking
      })
    );

    this.clientBankAccountForm.get('accountNumber').valueChanges.pipe(
      untilComponentDestroyed(this),
      distinctUntilChanged(),
      debounceTime(300)
    )
      .subscribe(_ => this.clientBankAccountForm.get('confirmAccountNumber').updateValueAndValidity());

    this.clientBankAccountForm.get('paymentType').valueChanges.pipe(
      untilComponentDestroyed(this),
      filter(paymentType => !!paymentType),
      distinctUntilChanged()
    ).subscribe(
      paymentType => this.usAccountPaymentType(paymentType)
    );
  }

  private resetFormWithoutCountry() {
    const formControls = this.clientBankAccountForm.controls;
    this.resetControlsAndRemoveValidators([
      formControls.bankCode,
      formControls.bankName,
      formControls.accountNumber,
      formControls.confirmAccountNumber,
      formControls.memo,
      formControls.swift,
      formControls.bankBranchCode,
      formControls.bankBranchName,
      formControls.bsb,
      formControls.canadaInstitutionNumber,
      formControls.canadaTransitNumber,
      formControls.ifsc,
      formControls.iban,
      formControls.ukSortCode,
      formControls.usabaRoutingNumber,
      formControls.nonACH,
      formControls.frn,
      formControls.checkingOrSavingsType
    ]);
  }

  private resetControlsAndRemoveValidators(controls: AbstractControl[]) {
    controls.forEach(control => {
      control.reset({value: null, disabled: true});
      control.setValidators([]);
      control.updateValueAndValidity();
    });
  }

  private initFormDataToSelectedCountry() {
    if (this.formIsSet === true) {
      this.resetFormWithoutCountry();
      this.formIsSet = false;
    }
    if (this.bankAccountModel) {
      this.setFormControlsFromModel(this.bankAccountModel);
      this.formIsSet = true;
    } else {
      this.resetFormWithoutCountry();
    }
    this.setFieldsAccordingToSelectedCountry();
    this.formReady.emit();
  }

  private setFieldsAccordingToSelectedCountry(disableLocalFields = false) {

    // If form is not read only
    if (!this.isReadOnlyMode) {
      switch (this.selectedCountry.code) {
        case TerraUtils.consts.countryCode.IL:
          if (this.showInternationalFields === true) {
            // international fields shown, domestic should be cleaned
            this.resetILDomesticFields();
            this.setInternationalFields();
          } else {
            this.resetInternationalFields();
            this.setILDomesticFields();
          }
          break;
        case TerraUtils.consts.countryCode.GB:
          if (this.showInternationalFields === true) {
            // international fields shown, domestic should be cleaned
            this.resetUKDomesticFields();
            this.setInternationalFields();
          } else {
            this.resetInternationalFields();
            this.setUKDomesticFields();
          }
          break;
        case TerraUtils.consts.countryCode.US:
          this.setUSFields();
          break;
        case TerraUtils.consts.countryCode.AU:
          this.setAUFields();
          break;
        case TerraUtils.consts.countryCode.CA:
          this.setCAFields();
          break;
        case TerraUtils.consts.countryCode.ZA:
          this.setZAFields();
          break;
        case TerraUtils.consts.countryCode.IN:
          this.setINFields();
          break;
        case TerraUtils.consts.countryCode.BE:
        case TerraUtils.consts.countryCode.FR:
        case TerraUtils.consts.countryCode.DE:
        case TerraUtils.consts.countryCode.IT:
        case TerraUtils.consts.countryCode.NL:
        case TerraUtils.consts.countryCode.RO:
        case TerraUtils.consts.countryCode.ES:
        case TerraUtils.consts.countryCode.CH:
        case TerraUtils.consts.countryCode.AT:
        case TerraUtils.consts.countryCode.CY:
        case TerraUtils.consts.countryCode.BG:
        case TerraUtils.consts.countryCode.HR:
        case TerraUtils.consts.countryCode.DK:
        case TerraUtils.consts.countryCode.EE:
        case TerraUtils.consts.countryCode.FI:
        case TerraUtils.consts.countryCode.GR:
        case TerraUtils.consts.countryCode.HU:
        case TerraUtils.consts.countryCode.IS:
        case TerraUtils.consts.countryCode.IE:
        case TerraUtils.consts.countryCode.LV:
        case TerraUtils.consts.countryCode.LI:
        case TerraUtils.consts.countryCode.LT:
        case TerraUtils.consts.countryCode.LU:
        case TerraUtils.consts.countryCode.MT:
        case TerraUtils.consts.countryCode.MC:
        case TerraUtils.consts.countryCode.NO:
        case TerraUtils.consts.countryCode.PL:
        case TerraUtils.consts.countryCode.PT:
        case TerraUtils.consts.countryCode.SM:
        case TerraUtils.consts.countryCode.SK:
        case TerraUtils.consts.countryCode.SI:
        case TerraUtils.consts.countryCode.SE:
        case TerraUtils.consts.countryCode.CZ:
        case TerraUtils.consts.countryCode.TR:
        case TerraUtils.consts.countryCode.GE:
        case TerraUtils.consts.countryCode.AE:
        case TerraUtils.consts.countryCode.UA:
          this.setInternationalFields();
          break;
        default:
          this.setDefaultFields();
          // Keeping this here as it might be useful if we ever decide to do a complete overhaul of the logic and set ALL validation according to validationService data
          //
          // const fields = this.validationService.getBankAccountFields()[0].fields;
          // fields.forEach(field => {
          //   const formControl = this.clientBankAccountForm.get(BankAccountFieldType.matchingFieldName(field.fieldType)) as FormControl;
          //   if (formControl) {
          //     formControl.setValidators(field.required ? Validators.required : []);
          //     formControl.updateValueAndValidity();
          //   }
          // });
          break;
      }

      if (this.isForceBankNameAndBranchCode && this.selectedCountry.code !== TerraUtils.consts.countryCode.US) {
        this.setBankNameAndBranchCodeFields();
      }

      this.clientBankAccountForm.updateValueAndValidity();
      // If read only, only set missing tax withholding related fields
    } else {
      if (this.isForceBankNameAndBranchCode && this.selectedCountry.code !== TerraUtils.consts.countryCode.US) {
        this.setMissingBankNameAndBranchCodeFields();
      }
    }
  }

  private populateForm(model: ClientBankAccountReqRes) {
    if (!model || !model.countryId) {
      this.formReady.emit();
      return;
    }
    this.showInternationalFields = model.swift && model.iban ? true : false;

    this.countries$.subscribe(countries => {
      const country = countries.find(c => c.id === model.countryId);
      if (country) {
        this.showInternalLoader = true;
        this.selectedCountry = country;
        this.clientBankAccountForm.get('countryId').setValue(country.id, {emitEvent: false});
        // Tell the validation service that it should use the selected country for validation
        this.validationService.setCountry(country);
        this.helperService.getBankAccountFields(country.code).subscribe(fields => {
          this.validationService.setBankAccountFields(fields);
          this.helperService.emitCountryChanged(country);
          this.initFormDataToSelectedCountry();
          if (this.selectedCountry.code === TerraUtils.consts.countryCode.IL) {
            this.setBankName(model.bankName);
          }
          this.showInternalLoader = false;
        });
      }
    });
  }

  private setFormControlsFromModel(model: ClientBankAccountReqRes) {
    if (!model || !model.countryId) {
      return;
    }

    this.accountNumberDraft = model.accountNumber || '';
    this.confirmAccountNumberDraft = model.accountNumber || '';

    const formControls = this.clientBankAccountForm.controls;
    formControls.paymentType.setValue(model.paymentType);
    formControls.paymentType.updateValueAndValidity();
    formControls.bankCode.setValue(model.bankCode);
    formControls.bankName.setValue(model.bankName);
    if (this.selectedCountry.code === TerraUtils.consts.countryCode.IL) {
      this.setBankName(model.bankName);
    }
    formControls.bankBranchCode.setValue(model.bankBranchCode);
    formControls.bankBranchName.setValue(model.bankBranchName);
    formControls.accountNumber.setValue(model.accountNumber);
    formControls.confirmAccountNumber.setValue(model.accountNumber);
    formControls.swift.setValue(model.swift);
    formControls.bsb.setValue(model.bsb);
    formControls.canadaInstitutionNumber.setValue(model.canadaInstitutionNumber);
    formControls.canadaTransitNumber.setValue(model.canadaTransitNumber);
    formControls.ifsc.setValue(model.ifsc);
    formControls.iban.setValue(model.iban);
    formControls.ukSortCode.setValue(model.ukSortCode);
    formControls.nonACH.setValue(model.usabaRoutingNumber || (!model.usabaRoutingNumber && !model.swift && !model.fedWireRoutingNumber) ? false : true);
    formControls.usabaRoutingNumber.setValue(model.usabaRoutingNumber);
    formControls.memo.setValue(model.memo);
    formControls.checkingOrSavingsType.setValue(model.checkingOrSavingsType);

    if (formControls.nonACH.value) {
      formControls.usabaRoutingNumber?.disable();
    }
    formControls.preferredCurrency.setValue(model.preferredCurrency);
    formControls.frn.setValue(model.fedWireRoutingNumber);
    this.showInternationalFields = model.swift && model.iban ? true : false;
  }

  private setInternationalFields() {
    const formControls = this.clientBankAccountForm.controls;
    formControls.iban.setValidators([Validators.required]);
    formControls.iban.updateValueAndValidity();
    formControls.iban.enable();
    formControls.swift.setValidators([Validators.required]);
    formControls.swift.updateValueAndValidity();
    formControls.swift.enable();
  }

  private resetInternationalFields() {
    const formControls = this.clientBankAccountForm.controls;

    formControls.iban.reset({value: null, disabled: true});
    formControls.iban.setValidators([]);
    formControls.iban.updateValueAndValidity();
    formControls.swift.reset({value: null, disabled: true});
    formControls.swift.setValidators([]);
    formControls.swift.updateValueAndValidity();
    this.resetUSDomesticFields();
  }

  private setILDomesticFields() {
    const formControls = this.clientBankAccountForm.controls;
    formControls.bankName.setValidators([Validators.required]);
    formControls.bankName.updateValueAndValidity();
    formControls.bankName.enable();
    formControls.bankCode.setValidators([Validators.required]);
    formControls.bankCode.updateValueAndValidity();
    formControls.bankCode.enable();
    formControls.bankBranchCode.setValidators([Validators.required]);
    formControls.bankBranchCode.updateValueAndValidity();
    formControls.bankBranchCode.enable();
    formControls.accountNumber.setValidators([Validators.required]);
    formControls.accountNumber.updateValueAndValidity();
    formControls.accountNumber.enable();
    this.resetUSDomesticFields();
  }

  private resetILDomesticFields() {
    const formControls = this.clientBankAccountForm.controls;
    formControls.bankName.reset({value: null, disabled: true});
    formControls.bankName.setValidators([]);
    formControls.bankName.updateValueAndValidity();
    formControls.bankCode.reset({value: null, disabled: true});
    formControls.bankCode.setValidators([]);
    formControls.bankCode.updateValueAndValidity();
    formControls.bankBranchCode.reset({value: null, disabled: true});
    formControls.bankBranchCode.setValidators([]);
    formControls.bankBranchCode.updateValueAndValidity();
    formControls.accountNumber.reset({value: null, disabled: true});
    formControls.accountNumber.setValidators([]);
    formControls.accountNumber.updateValueAndValidity();
  }

  private setUKDomesticFields() {
    const formControls = this.clientBankAccountForm.controls;
    formControls.ukSortCode.setValidators([Validators.required]);
    formControls.ukSortCode.updateValueAndValidity();
    formControls.ukSortCode.enable();
    formControls.accountNumber.setValidators([Validators.required]);
    formControls.accountNumber.updateValueAndValidity();
    formControls.accountNumber.enable();
    this.resetUSDomesticFields();
  }

  private resetUKDomesticFields() {
    const formControls = this.clientBankAccountForm.controls;
    formControls.ukSortCode.reset({value: null, disabled: true});
    formControls.ukSortCode.setValidators([]);
    formControls.ukSortCode.updateValueAndValidity();
    formControls.accountNumber.reset({value: null, disabled: true});
    formControls.accountNumber.setValidators([]);
    formControls.accountNumber.updateValueAndValidity();
    this.resetUSDomesticFields();
  }

  private setBankNameAndBranchCodeFields() {
    const formControls = this.clientBankAccountForm.controls;
    formControls.bankName.patchValue(formControls.bankName.value); // Hack (https://github.com/angular/angular/issues/10036)
    formControls.bankName.setValidators([Validators.required]);
    formControls.bankName.updateValueAndValidity();
    formControls.bankName.enable();
    formControls.bankBranchCode.patchValue(formControls.bankBranchCode.value); // Hack (https://github.com/angular/angular/issues/10036)
    formControls.bankBranchCode.setValidators([Validators.required]);
    formControls.bankBranchCode.updateValueAndValidity();
    formControls.bankBranchCode.enable();
  }

  private setMissingBankNameAndBranchCodeFields() {
    const formControls = this.clientBankAccountForm.controls;
    if (!formControls.bankName.value) {
      formControls.bankName.setValidators([Validators.required]);
      formControls.bankName.updateValueAndValidity();
      formControls.bankName.enable();
      this.isIgnoreReadOnlyBankName = true;
    }
    if (!formControls.bankBranchCode.value) {
      formControls.bankBranchCode.setValidators([Validators.required]);
      formControls.bankBranchCode.updateValueAndValidity();
      formControls.bankBranchCode.enable();
      this.isIgnoreReadOnlyBankBranchCode = true;
    }
  }

  private setUSFields() {
    const formControls = this.clientBankAccountForm.controls;
    if (!formControls.paymentType.value) {
      formControls.paymentType.setValue(PaymentType.Ach);
    }
  }

  resetUSDomesticFields() {
    const formControls = this.clientBankAccountForm.controls;
    formControls.paymentType.clearValidators();
    // formControls.paymentType.disable();
    formControls.paymentType.updateValueAndValidity();
    formControls.confirmAccountNumber.disable();
    formControls.swift.clearValidators();
    formControls.swift.disable();
    formControls.swift.updateValueAndValidity();
    formControls.frn.clearValidators();
    formControls.frn.disable();
    formControls.frn.updateValueAndValidity();
    formControls.usabaRoutingNumber.clearValidators();
    formControls.usabaRoutingNumber.disable();
    formControls.usabaRoutingNumber.updateValueAndValidity();
    formControls.iban.clearValidators();
    formControls.iban.disable();
    formControls.iban.updateValueAndValidity();
  }

  usAccountPaymentType(paymentType: PaymentType) {
    const formControls = this.clientBankAccountForm.controls;

    const confirmAccountNumberDraft = formControls.confirmAccountNumber.value;
    const accountNumberDraft = formControls.accountNumber.value;

    formControls.accountNumber.setValidators([Validators.required]);
    formControls.accountNumber.enable();
    formControls.confirmAccountNumber.setValidators([CustomValidators.confirmedValidator(this.clientBankAccountForm.get('accountNumber') as UntypedFormControl)]);
    formControls.confirmAccountNumber.enable();
    formControls.memo.enable();

    switch (paymentType) {
      case PaymentType.Ach:
        formControls.usabaRoutingNumber.setValidators([Validators.required]);
        formControls.usabaRoutingNumber.enable();
        formControls.iban.disable();
        formControls.swift.disable();
        formControls.frn.disable();
        break;
      case PaymentType.Wire:
        formControls.frn.setValidators([Validators.required]);
        formControls.frn.enable();
        formControls.usabaRoutingNumber.disable();
        formControls.iban.disable();
        formControls.swift.disable();
        break;
    }

    formControls.accountNumber.setValue(this.accountNumberDraft);
    formControls.confirmAccountNumber.reset(this.confirmAccountNumberDraft);
    formControls.frn.updateValueAndValidity();
    formControls.usabaRoutingNumber.updateValueAndValidity();
    formControls.accountNumber.updateValueAndValidity();
    formControls.confirmAccountNumber.updateValueAndValidity();

    this.accountNumberDraft = accountNumberDraft;
    this.confirmAccountNumberDraft = confirmAccountNumberDraft;
  }

  private minimumNumValuesRequired(minimum: number, controlsPath: Array<string>): ValidatorFn {
    return (formGroup: FormGroup): { [key: string]: boolean } | null => {
      let valid = false;

      let filledNumber = 0;
      for (const controlPath of controlsPath) {
        const control: FormControl = (formGroup.get(controlPath) as FormControl);
        if (control.value) {
          filledNumber++;
        }
        if (filledNumber >= minimum) {
          valid = true;
          break;
        }
      }
      return valid ? null : {minNumValuesRequired: true};
    };
  }

  private setAUFields() {
    this.resetUSDomesticFields();
    const formControls = this.clientBankAccountForm.controls;
    formControls.bankName.setValidators([Validators.required]);
    formControls.bankName.updateValueAndValidity();
    formControls.bankName.enable();
    formControls.accountNumber.setValidators([Validators.required]);
    formControls.accountNumber.updateValueAndValidity();
    formControls.accountNumber.enable();
    formControls.bsb.setValidators([Validators.required]);
    formControls.bsb.updateValueAndValidity();
    formControls.bsb.enable();
    formControls.swift.setValidators([Validators.required]);
    formControls.swift.updateValueAndValidity();
    formControls.swift.enable();

  }

  private setCAFields() {
    this.resetUSDomesticFields();
    const formControls = this.clientBankAccountForm.controls;
    formControls.bankName.setValidators([Validators.required]);
    formControls.bankName.updateValueAndValidity();
    formControls.bankName.enable();
    formControls.accountNumber.setValidators([Validators.required]);
    formControls.accountNumber.updateValueAndValidity();
    formControls.accountNumber.enable();
    formControls.canadaTransitNumber.setValidators([Validators.required]);
    formControls.canadaTransitNumber.updateValueAndValidity();
    formControls.canadaTransitNumber.enable();
    formControls.canadaInstitutionNumber.setValidators([Validators.required]);
    formControls.canadaInstitutionNumber.updateValueAndValidity();
    formControls.canadaInstitutionNumber.enable();
    formControls.swift.setValidators([Validators.required]);
    formControls.swift.updateValueAndValidity();
    formControls.swift.enable();

  }

  private setZAFields() {
    this.resetUSDomesticFields();
    const formControls = this.clientBankAccountForm.controls;
    formControls.bankName.setValidators([Validators.required]);
    formControls.bankName.updateValueAndValidity();
    formControls.bankName.enable();
    formControls.accountNumber.setValidators([Validators.required]);
    formControls.accountNumber.updateValueAndValidity();
    formControls.accountNumber.enable();
    formControls.bankBranchCode.setValidators([]);
    formControls.bankBranchCode.updateValueAndValidity();
    formControls.bankBranchCode.enable();
    formControls.swift.setValidators([Validators.required]);
    formControls.swift.updateValueAndValidity();
    formControls.swift.enable();

  }

  private setINFields() {
    this.resetUSDomesticFields();
    const formControls = this.clientBankAccountForm.controls;
    formControls.bankName.setValidators([Validators.required]);
    formControls.bankName.updateValueAndValidity();
    formControls.bankName.enable();
    formControls.accountNumber.setValidators([Validators.required]);
    formControls.accountNumber.updateValueAndValidity();
    formControls.accountNumber.enable();
    formControls.swift.setValidators([Validators.required]);
    formControls.swift.updateValueAndValidity();
    formControls.swift.enable();

  }

  private setDefaultFields() {
    const formControls = this.clientBankAccountForm.controls;
    formControls.bankName.setValidators([Validators.required]);
    formControls.bankName.updateValueAndValidity();
    formControls.bankName.enable();
    formControls.accountNumber.setValidators([Validators.required]);
    formControls.accountNumber.updateValueAndValidity();
    formControls.accountNumber.enable();
    formControls.swift.setValidators([Validators.required]);
    formControls.swift.updateValueAndValidity();
    formControls.swift.enable();
  }

  private updateModelWithPreferredCurrency(country: CountryModel) {
    this.currencies$.pipe(take(1), untilComponentDestroyed(this)).subscribe(currencies => {
      // We only update the model, and the form value is updated in initFormDataToSelectedCountry method
      this.bankAccountModel.preferredCurrency = currencies.find(c => c.id === country.defaultCurrency.id);
    });
  }
}
